import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Rnd, DraggableData } from 'react-rnd';

const TRANSITION_MS = 300;

export const FLOATING_PANEL_Z_INDEX = 9999;

interface UseFloatingPanelOptions {
  panelWidth: number;
  isOpen: boolean;
  dragStyleId: string;
  currentDifferenceId?: string;
  rndContainerClassName: string;
  onAutoOpen?: () => void;
}

export function useFloatingPanel({
  panelWidth,
  isOpen,
  dragStyleId,
  currentDifferenceId,
  rndContainerClassName,
  onAutoOpen,
}: UseFloatingPanelOptions) {
  const [documentsPanelHeight, setDocumentsPanelHeight] = useState<number | null>(null);
  const [documentsPanelElement, setDocumentsPanelElement] = useState<HTMLElement | null>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const positionRef = useRef({ x: 0, y: 0 });
  const [size, setSize] = useState({ width: panelWidth, height: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const isDraggingRef = useRef(false);
  const previousDifferenceId = useRef<string | null>(null);
  const rndRef = useRef<Rnd>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [resizeCounter, setResizeCounter] = useState(0);
  const isOpenCloseAnimatingRef = useRef(false);
  const openCloseAnimTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useLayoutEffect(() => {
    const element = document.getElementById('documents-panel');
    if (element) setDocumentsPanelElement(element);
  }, []);

  const measureDocumentsPanelHeight = () => {
    const el = document.getElementById('documents-panel') as HTMLElement;
    if (el && el.offsetHeight > 0) {
      setDocumentsPanelHeight(el.offsetHeight);
    }
  };

  useLayoutEffect(() => {
    measureDocumentsPanelHeight();
  }, [isOpen]);

  useEffect(() => {
    if (!documentsPanelElement) return;

    let resizeObserver: ResizeObserver | null = null;

    const handleResize = () => {
      try {
        measureDocumentsPanelHeight();
        setResizeCounter((c) => c + 1);
      } catch (error) {
        console.error('Error measuring documents panel height:', error);
      }
    };

    resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(documentsPanelElement);
    window.addEventListener('resize', handleResize);

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [documentsPanelElement]);

  const applySizeAndPosition = () => {
    // Unmeasured means we would size the panel to zero height.
    if (documentsPanelHeight === null) return null;

    const headerHeight = headerRef.current?.offsetHeight ?? null;
    const currentX = positionRef.current.x;

    // Open and closed share a bottom edge, so height and y animate together
    // rather than collapsing and then jumping.
    let newSize: { width: number; height: number };
    let newPosition: { x: number; y: number };

    if (isOpen) {
      newSize = { width: panelWidth, height: documentsPanelHeight };
      newPosition = { x: currentX, y: 0 };
    } else if (headerHeight !== null) {
      newSize = { width: panelWidth, height: headerHeight };
      newPosition = { x: currentX, y: Math.max(0, documentsPanelHeight - headerHeight) };
    } else {
      newSize = { width: panelWidth, height: 0 };
      newPosition = { x: currentX, y: 0 };
    }

    setSize(newSize);
    setPosition(newPosition);
    positionRef.current = newPosition;

    if (rndRef.current) {
      rndRef.current.updatePosition({ x: newPosition.x, y: newPosition.y });
      rndRef.current.updateSize({ width: newSize.width, height: newSize.height });
    }

    return { newSize, newPosition };
  };

  // Deferred callers must read the latest measurements, not the ones captured when
  // their effect was set up.
  const applySizeAndPositionRef = useRef(applySizeAndPosition);
  applySizeAndPositionRef.current = applySizeAndPosition;

  useEffect(() => {
    if (!headerRef.current || !documentsPanelElement) return;

    let resizeObserver: ResizeObserver | null = null;

    const handleHeaderResize = () => {
      if (isDraggingRef.current || isOpenCloseAnimatingRef.current) return;
      applySizeAndPositionRef.current();
    };

    resizeObserver = new ResizeObserver(handleHeaderResize);
    resizeObserver.observe(headerRef.current);

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [documentsPanelElement]);

  // Header reflow mid-transition would re-apply y after the height has already
  // animated, so corrections are locked out until the transition ends.
  useEffect(() => {
    isOpenCloseAnimatingRef.current = true;
    if (openCloseAnimTimeoutRef.current) {
      clearTimeout(openCloseAnimTimeoutRef.current);
    }
    openCloseAnimTimeoutRef.current = setTimeout(() => {
      isOpenCloseAnimatingRef.current = false;
      if (!isDraggingRef.current) {
        applySizeAndPositionRef.current();
      }
    }, TRANSITION_MS);

    return () => {
      if (openCloseAnimTimeoutRef.current) {
        clearTimeout(openCloseAnimTimeoutRef.current);
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (currentDifferenceId !== previousDifferenceId.current) {
      const resetPos = { x: 0, y: 0 };
      setPosition(resetPos);
      positionRef.current = resetPos;
      isDraggingRef.current = false;
      setIsDragging(false);
      previousDifferenceId.current = currentDifferenceId || null;
      if (!isOpen && onAutoOpen) onAutoOpen();
    }
  }, [currentDifferenceId, isOpen, onAutoOpen]);

  useLayoutEffect(() => {
    if (isDragging) return;

    const applied = applySizeAndPosition();

    // Transitions stay off until the panel has real geometry, so the first open
    // does not animate out of the zero-height state.
    if (applied && !isMounted) {
      requestAnimationFrame(() => setIsMounted(true));
    }
  }, [isOpen, documentsPanelHeight, resizeCounter]);

  useEffect(() => {
    return () => {
      handleInteractionStyles(false);
      document.body.style.cursor = '';
      const dragStyle = document.getElementById(dragStyleId);
      if (dragStyle) dragStyle.remove();
      if (openCloseAnimTimeoutRef.current) {
        clearTimeout(openCloseAnimTimeoutRef.current);
      }
    };
  }, []);

  const handleInteractionStyles = (isStart: boolean) => {
    document.body.style.userSelect = isStart ? 'none' : '';
    document.body.style.pointerEvents = isStart ? 'none' : '';
  };

  const updatePosition = (newPos: { x: number; y: number }) => {
    setPosition(newPos);
    positionRef.current = newPos;
  };

  const rndHandlers = {
    onDragStart: () => {
      isDraggingRef.current = true;
      setIsDragging(true);
      handleInteractionStyles(true);
      const style = document.createElement('style');
      style.id = dragStyleId;
      style.innerHTML = '* { cursor: grabbing !important; }';
      document.head.appendChild(style);
    },
    onDrag: (_e: any, d: DraggableData) => {
      updatePosition({ x: d.x, y: d.y });
    },
    onDragStop: (_e: any, d: DraggableData) => {
      isDraggingRef.current = false;
      setIsDragging(false);
      handleInteractionStyles(false);
      updatePosition({ x: d.x, y: d.y });
      const style = document.getElementById(dragStyleId);
      if (style) style.remove();
    },
  };

  const rndStyle: React.CSSProperties = {
    position: 'absolute',
    zIndex: FLOATING_PANEL_Z_INDEX,
    transition:
      !isDragging && isMounted
        ? `transform ${TRANSITION_MS}ms ease-out, width ${TRANSITION_MS}ms ease-out, height ${TRANSITION_MS}ms ease-out`
        : 'none',
    cursor: 'default',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
  };

  const rndClassName = `${rndContainerClassName} ${isDragging ? 'dragging' : ''}`;

  return {
    documentsPanelElement,
    documentsPanelHeight,
    headerRef,
    rndRef,
    position,
    size,
    isDragging,
    isMounted,
    rndHandlers,
    rndStyle,
    rndClassName,
  };
}
